import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../lib/toast';
import { useCreateBusinessProfile, useUpdateBusinessProfile } from '../hooks/useBusinessProfiles';
import { useBusinessTypes, useGenerateContentStrategy, useAllPlatforms } from '../hooks/useCommon';
import { useGenerateBusinessInfoFromWebsite } from '../hooks/useBusinessProfiles';
import { useUserAvailablePlatforms } from '../lib/planEntitlements';
import { useUsage } from '../hooks/useCommon';
import { useCurrentUser } from '../hooks/useAuth';
import { useBusinessProfileForm, type BusinessProfileFormData } from '../hooks/useBusinessProfileForm';
import PageSection from '../components/PageSection';
import BasicInfoStep from './business-profile/BasicInfoStep';
import ContentStrategyStep from './business-profile/ContentStrategyStep';
import PlatformsStep from './business-profile/PlatformsStep';
import StrategyConfirmationDialog from '../components/features/business-profile/StrategyConfirmationDialog';
import QuotaConfirmationDialog from '../components/features/business-profile/QuotaConfirmationDialog';
import SaveDiscardDialog from '../components/features/business-profile/SaveDiscardDialog';

// const steps = [
//   'Basic Information',
//   'Content Strategy',
//   'Preferred Platforms'
// ];

function BusinessProfileForm() {
  const { t } = useTranslation('profile');
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [strategyError, setStrategyError] = useState('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [strategyConfirmDialogOpen, setStrategyConfirmDialogOpen] = useState(false);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [generatedStrategy, setGeneratedStrategy] = useState<{
    target_audience: string[];
    key_messages: string[];
    content_goals: string[];
    brand_voice?: string[];
  } | null>(null);
  const [saveDiscardDialogOpen, setSaveDiscardDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const initialFormDataRef = useRef<string | null>(null);
  const isSavingRef = useRef(false);

  const isEditMode = !!id;
  const profileId = id ? parseInt(id) : null;

  // Queries
  const { data: businessTypes, isLoading: loadingBusinessTypes } = useBusinessTypes();
  const { data: allPlatforms } = useAllPlatforms();
  const { data: availablePlatforms } = useUserAvailablePlatforms();
  const { data: usage } = useUsage();
  const { data: currentUser } = useCurrentUser();
  
  const createProfile = useCreateBusinessProfile();
  const updateProfile = useUpdateBusinessProfile();
  const generateStrategy = useGenerateContentStrategy();
  const generateBusinessInfo = useGenerateBusinessInfoFromWebsite();

  // Calculate quota availability for content strategy generation
  const quotaInfo = useMemo(() => {
    if (!usage) {
      return { isAvailable: true, isDisabled: false, resetMessage: null };
    }

    // Check if content strategy quota fields exist in usage response
    // If they don't exist, assume quota is available (don't block users unnecessarily)
    const hasContentStrategyQuotaInfo = usage.content_strategy_monthly_limit !== undefined;
    if (!hasContentStrategyQuotaInfo) {
      return { isAvailable: true, isDisabled: false, resetMessage: null };
    }

    // Use content_strategy_* fields for content strategy quota
    const monthlyRemaining = usage.content_strategy_monthly_remaining ?? 0;
    const isUnlimited = usage.content_strategy_unlimited ?? false;
    const dailyLimit = usage.content_strategy_daily_limit;
    const dailyUsed = usage.content_strategy_daily_used ?? 0;
    const dailyRemaining = usage.content_strategy_daily_remaining ?? (dailyLimit !== undefined ? Math.max(0, (dailyLimit || 0) - dailyUsed) : undefined);
    const monthlyUsed = usage.content_strategy_monthly_used ?? 0;
    const monthlyLimit = usage.content_strategy_monthly_limit;

    // Check if quota is available
    const monthlyAvailable = isUnlimited || monthlyRemaining > 0 || monthlyRemaining === -1;
    const dailyAvailable = dailyLimit === undefined || dailyRemaining === undefined || dailyRemaining > 0;
    const isAvailable = monthlyAvailable && dailyAvailable;

    // Determine reset message if quota is insufficient
    let resetMessage: string | null = null;
    if (!isAvailable) {
      const dailyExhausted = dailyLimit !== undefined && dailyRemaining !== undefined && dailyRemaining === 0;
      const monthlyExhausted = !isUnlimited && monthlyLimit !== undefined && monthlyUsed >= monthlyLimit;
      
      if (monthlyExhausted || (dailyExhausted && monthlyExhausted)) {
        resetMessage = 'You can use this feature again next month';
      } else if (dailyExhausted) {
        resetMessage = 'You can use this feature again tomorrow';
      }
    }

    return {
      isAvailable,
      isDisabled: !isAvailable,
      resetMessage,
    };
  }, [usage]);

  // Use the custom hook for all form state and logic
  const {
    formData,
    setFormData,
    tagInputs,
    setTagInputs,
    addressInput,
    setAddressInput,
    strategyGenerated,
    setStrategyGenerated,
    handleAddressSelect,
    handleTagInputChange,
    handleTagAdd,
    handleTagRemove,
    handleTagKeyPress,
    handlePlatformChange,
    validateStep,
  } = useBusinessProfileForm(profileId);

  // Track initial form data for dirty state detection
  useEffect(() => {
    if (!initialFormDataRef.current && formData.business_name) {
      initialFormDataRef.current = JSON.stringify(formData);
    }
  }, [formData]);

  // Check if form has unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialFormDataRef.current) return false;
    const currentData = JSON.stringify(formData);
    return currentData !== initialFormDataRef.current;
  };

  // Navigation blocking - now works with data router
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }; nextLocation: { pathname: string } }) =>
      hasUnsavedChanges() && !isSavingRef.current && currentLocation.pathname !== nextLocation.pathname
  );

  // Handle browser navigation (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges() && !isSavingRef.current) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formData]);

  // Handle React Router navigation blocking
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setPendingNavigation(() => blocker.proceed);
      setSaveDiscardDialogOpen(true);
    }
  }, [blocker.state, blocker.proceed]);


  const handleSaveAndNavigate = async () => {
    isSavingRef.current = true;
    setSaveDiscardDialogOpen(false);
    try {
      // Create a synthetic form event for handleSubmit
      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
      } as React.FormEvent<HTMLFormElement>;
      await handleSubmit(syntheticEvent);
      // Update initial data ref after successful save
      initialFormDataRef.current = JSON.stringify(formData);
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
    } catch (error) {
      // console.error('Save error:', error);
      setSaveDiscardDialogOpen(true);
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleDiscardAndNavigate = () => {
    setSaveDiscardDialogOpen(false);
    initialFormDataRef.current = JSON.stringify(formData);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
    blocker.reset();
  };

  const handleCancelNavigation = () => {
    setSaveDiscardDialogOpen(false);
    setPendingNavigation(null);
    blocker.reset();
  };

  const handleNext = async () => {
    // Show validation errors when user tries to proceed
    setShowValidationErrors(true);
    
    if (validateStep(activeStep)) {
      // If moving from Business Info to Content Strategy
      if (activeStep === 0) {
        // Create profile if it doesn't exist (save business info)
        const currentProfileId = await ensureProfileExists();
        if (!currentProfileId) {
          return;
        }

        // Update profile with latest form data if it already exists
        if (profileId && profileId === currentProfileId) {
          try {
            const profileData = {
              business_name: formData.business_name,
              business_type_id: formData.business_type_id || 0,
              description: formData.description,
              brand_voice: Array.isArray(formData.brand_voice)
                ? formData.brand_voice.filter((t: string) => t && t.trim()).join(', ')
                : (formData.brand_voice as unknown as string | undefined),
              products_services: Array.isArray(formData.products_services)
                ? formData.products_services.filter((t: string) => t && t.trim()).join(', ')
                : '',
              website_url: formData.website_url,
              address_line_1: formData.address_line_1,
              address_line_2: formData.address_line_2,
              city: formData.city,
              state_province: formData.state_province,
              postal_code: formData.postal_code,
              country: formData.country,
              target_audience: formData.target_audience,
              key_messages: formData.key_messages,
              content_goals: formData.content_goals,
              preferred_platform_ids: formData.preferred_platform_ids,
            };
            await updateProfile.mutateAsync({ id: currentProfileId.toString(), data: profileData });
            // Update initial data ref after successful save to prevent false "unsaved changes" dialog
            initialFormDataRef.current = JSON.stringify(formData);
          } catch (error) {
            // Continue anyway - profile was created, just couldn't update
          }
        }

        // Check if profile already has strategy data (editing existing profile)
        const hasStrategyData = 
          (formData.target_audience && formData.target_audience.length > 0) ||
          (formData.key_messages && formData.key_messages.length > 0) ||
          (formData.content_goals && formData.content_goals.length > 0);
        
        // If existing data, mark as generated so form shows directly
        if (hasStrategyData) {
          setStrategyGenerated(true);
        }
        // Otherwise, user will see the "Generate with AI" or "enter manually" options
        
        setActiveStep(1);
      } else {
        // Moving to other steps
        setActiveStep((prev: number) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev: number) => Math.max(0, prev - 1));
  };


  const applyStrategyResults = (strategy: { target_audience: string[]; key_messages: string[]; content_goals: string[]; brand_voice?: string[] }) => {
    setFormData((prev: BusinessProfileFormData) => ({
      ...prev,
      target_audience: strategy.target_audience || [],
      key_messages: strategy.key_messages || [],
      content_goals: strategy.content_goals || [],
      brand_voice: strategy.brand_voice || prev.brand_voice || [],
    }));
    setStrategyGenerated(true);
    // Show success toast when strategy is applied
    toast.success('contentStrategy.appliedSuccess');
  };

  const handleGenerateStrategy = async () => {
    if (!profileId) return;

    // Show quota confirmation dialog before making API call
    setQuotaDialogOpen(true);
  };

  const handleQuotaConfirm = async () => {
    if (!profileId) {
      setQuotaDialogOpen(false);
      return;
    }

    setQuotaDialogOpen(false);
    setStrategyLoading(true);
    setStrategyError('');
    try {
      const strategyResult = await generateStrategy.mutateAsync({ business_profile_id: profileId });
      
      // Check if we need to ask for confirmation
      const hasExistingData = 
        (formData.target_audience && formData.target_audience.length > 0) ||
        (formData.key_messages && formData.key_messages.length > 0) ||
        (formData.content_goals && formData.content_goals.length > 0);
      
      if (hasExistingData) {
        // Store result and show confirmation dialog
        setGeneratedStrategy(strategyResult);
        setStrategyConfirmDialogOpen(true);
      } else {
        // No existing data, apply directly
        applyStrategyResults(strategyResult);
      }
    } catch (error: any) {
      // console.error('Generate strategy error:', error);
      // Extract error message from backend response for inline Alert display
      let errorMessage = 'Failed to generate content strategy';
      
      if (error?.response?.data) {
        const data = error.response.data;
        
        // Handle FastAPI validation errors (array of error objects)
        if (Array.isArray(data)) {
          errorMessage = data.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err?.msg) return err.msg;
            if (err?.message) return err.message;
            return JSON.stringify(err);
          }).join(', ');
        } else if (typeof data === 'object' && data.detail) {
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map((err: any) => {
              if (typeof err === 'string') return err;
              if (err?.msg) return err.msg;
              if (err?.message) return err.message;
              return JSON.stringify(err);
            }).join(', ');
          }
        } else if (data.message && typeof data.message === 'string') {
          errorMessage = data.message;
        }
      } else if (error?.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }
      
      setStrategyError(errorMessage);
    } finally {
      setStrategyLoading(false);
    }
  };

  const handleQuotaCancel = () => {
    setQuotaDialogOpen(false);
  };

  const handleConfirmStrategyReplace = () => {
    if (generatedStrategy) {
      applyStrategyResults(generatedStrategy);
      setStrategyConfirmDialogOpen(false);
      setGeneratedStrategy(null);
    }
  };

  const handleCancelStrategyReplace = () => {
    setStrategyConfirmDialogOpen(false);
    setGeneratedStrategy(null);
  };

  // Create profile early if needed (for website scraping before clicking Next)
  // Note: website_url is NOT saved here - it's only saved when user explicitly saves the profile
  // This allows users to try different URLs without committing them
  const ensureProfileExists = async (): Promise<number | null> => {
    if (profileId) {
      return profileId;
    }

    // Create profile with current form data (excluding website_url)
    if (!currentUser?.id) {
      console.error('Cannot create profile: user not authenticated');
      return null;
    }

    try {
      const profileData = {
        user_id: currentUser.id,
        business_name: formData.business_name || 'Draft Profile',
        business_type_id: formData.business_type_id || 0,
        description: formData.description || '',
        brand_voice: Array.isArray(formData.brand_voice)
          ? formData.brand_voice.filter((t: string) => t && t.trim()).join(', ')
          : (formData.brand_voice as unknown as string | undefined),
        products_services: Array.isArray(formData.products_services)
          ? formData.products_services.filter((t: string) => t && t.trim()).join(', ')
          : '',
        // website_url is intentionally excluded - only saved on explicit profile save
        address_line_1: formData.address_line_1 || '',
        address_line_2: formData.address_line_2 || '',
        city: formData.city || '',
        state_province: formData.state_province || '',
        postal_code: formData.postal_code || '',
        country: formData.country || '',
        target_audience: formData.target_audience,
        key_messages: formData.key_messages,
        content_goals: formData.content_goals,
        preferred_platform_ids: formData.preferred_platform_ids,
      };
      const result = await createProfile.mutateAsync(profileData);
      if (result?.id) {
        // Mark current form data as "saved" to prevent unsaved changes dialog
        initialFormDataRef.current = JSON.stringify(formData);
        // Update URL to include the new profile ID
        navigate(`/business-profiles/${result.id}`, { replace: true });
        return result.id;
      }
    } catch (error) {
      // console.error('Error creating profile for scraping:', error);
    }
    return null;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!currentUser?.id) {
      setError('You must be logged in to save a business profile');
      setLoading(false);
      return;
    }

    try {
      const profileData = {
        user_id: currentUser.id,
        business_name: formData.business_name,
        business_type_id: formData.business_type_id || 0,
        description: formData.description,
        brand_voice: Array.isArray(formData.brand_voice)
          ? formData.brand_voice.filter((t: string) => t && t.trim()).join(', ')
          : (formData.brand_voice as unknown as string | undefined),
        products_services: Array.isArray(formData.products_services)
          ? formData.products_services.filter((t: string) => t && t.trim()).join(', ')
          : '',
        website_url: formData.website_url,
        address_line_1: formData.address_line_1,
        address_line_2: formData.address_line_2,
        city: formData.city,
        state_province: formData.state_province,
        postal_code: formData.postal_code,
        country: formData.country,
        target_audience: formData.target_audience,
        key_messages: formData.key_messages,
        content_goals: formData.content_goals,
        preferred_platform_ids: formData.preferred_platform_ids,
      };

      if (isEditMode && profileId) {
        await updateProfile.mutateAsync({ id: profileId.toString(), data: profileData });
      } else {
        const result = await createProfile.mutateAsync(profileData);
        if (result?.id) {
          // Apply strategy if generated
          // Strategy application removed - not available in current API
        }
      }

      // Update initial data ref after successful save
      initialFormDataRef.current = JSON.stringify(formData);
      navigate('/dashboard');
    } catch (error: unknown) {
      // console.error('Submit error:', error);
      
      // Type guard for axios error
      interface AxiosError {
        response?: {
          data?: {
            detail?: unknown;
          };
        };
        message?: string;
      }
      
      const axiosError = error as AxiosError;
      const detail = axiosError?.response?.data?.detail ?? axiosError?.response?.data;
      let message: string;
      
      if (Array.isArray(detail)) {
        message = detail.map((d: unknown) => {
          if (typeof d === 'string') return d;
          if (d && typeof d === 'object') {
            const obj = d as { msg?: string; message?: string };
            return obj?.msg || obj?.message || JSON.stringify(d);
          }
          return JSON.stringify(d);
        }).join(', ');
      } else if (typeof detail === 'string') {
        message = detail;
      } else if (detail && typeof detail === 'object') {
        const obj = detail as { message?: string; error?: string };
        message = obj.message || obj.error || 'Failed to save business profile';
      } else {
        message = axiosError?.message || 'Failed to save business profile';
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => () => navigate('/dashboard'));
      setSaveDiscardDialogOpen(true);
    } else {
      navigate('/dashboard');
    }
  };

  if (loadingBusinessTypes) {
    return (
      <PageSection 
        title={isEditMode ? t('businessProfile.updateTitle') : t('businessProfile.createTitle')}
        subtitle={t('businessProfile.manageSubtitle')}
        hideTitleOnMobile
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </PageSection>
    );
  }

  return (
    <PageSection 
      title={isEditMode ? t('businessProfile.updateTitle') : t('businessProfile.createTitle')}
      subtitle={t('businessProfile.manageSubtitle')}
      hideTitleOnMobile
    >
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); void handleSubmit(e); }}>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {/* Step 1: Basic Information */}
                  <Step>
                    <StepLabel>{t('businessProfile.steps.businessInfo')}</StepLabel>
                    <StepContent sx={{ mt: 2 }}>
              <BasicInfoStep
                formData={formData}
                setFormData={setFormData}
                tagInputs={tagInputs}
                setTagInputs={setTagInputs}
                addressInput={addressInput}
                setAddressInput={setAddressInput}
                businessTypes={businessTypes || []}
                loadingBusinessTypes={loadingBusinessTypes}
                onNext={handleNext}
                onTagAdd={handleTagAdd}
                onTagRemove={handleTagRemove}
                onTagInputChange={handleTagInputChange}
                onTagKeyPress={handleTagKeyPress}
                onAddressSelect={handleAddressSelect}
                onGenerateBusinessInfo={async (url: string) => {
                  // Ensure profile exists before generating business info (create if needed)
                  const currentProfileId = await ensureProfileExists();
                  if (!currentProfileId) {
                    throw new Error('Unable to create profile for business info generation');
                  }
                  const result = await generateBusinessInfo.mutateAsync({ profileId: currentProfileId, url });
                  return result;
                }}
                profileId={profileId}
                isValid={showValidationErrors ? validateStep(0) : true}
              />
                    </StepContent>
                  </Step>

                  {/* Step 2: Content Strategy */}
                  <Step>
                    <StepLabel>{t('businessProfile.steps.contentStrategy')}</StepLabel>
                    <StepContent sx={{ mt: 2 }}>
              <ContentStrategyStep
                formData={formData}
                setFormData={setFormData}
                tagInputs={tagInputs}
                setTagInputs={setTagInputs}
                strategyLoading={strategyLoading}
                strategyGenerated={strategyGenerated}
                strategyError={strategyError}
                onGenerateStrategy={handleGenerateStrategy}
                onSkipGeneration={() => {
                  setStrategyGenerated(true);
                  setShowValidationErrors(false); // Don't show errors when entering manual mode
                }}
                onNext={handleNext}
                onBack={handleBack}
                onTagAdd={handleTagAdd}
                onTagRemove={handleTagRemove}
                onTagInputChange={handleTagInputChange}
                onTagKeyPress={handleTagKeyPress}
                isValid={showValidationErrors ? validateStep(1) : true}
                disabled={quotaInfo.isDisabled}
                quotaMessage={quotaInfo.resetMessage}
              />
                    </StepContent>
                  </Step>

                  {/* Step 3: Preferred Platforms */}
                  <Step>
                    <StepLabel>{t('businessProfile.steps.preferredPlatforms')}</StepLabel>
                    <StepContent sx={{ mt: 2 }}>
              <PlatformsStep
                formData={formData}
                allPlatforms={allPlatforms || []}
                availablePlatforms={availablePlatforms || []}
                loadingAllPlatforms={false}
                loadingAvailablePlatforms={false}
                onPlatformChange={handlePlatformChange}
                onNext={() => {
                  setShowValidationErrors(true);
                  const syntheticEvent = {
                    preventDefault: () => {},
                    stopPropagation: () => {},
                  } as React.FormEvent<HTMLFormElement>;
                  void handleSubmit(syntheticEvent);
                }}
                onBack={handleBack}
                isValid={showValidationErrors ? validateStep(2) : true}
              />
            </StepContent>
          </Step>
        </Stepper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            {t('businessProfile.buttons.cancel')}
                        </Button>
                        <Button
            type="button"
                          variant="contained"
            disabled={loading || (showValidationErrors && !validateStep(2))}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              setShowValidationErrors(true);
              // Convert mouse event to form event
              const syntheticEvent = {
                ...e,
                preventDefault: () => e.preventDefault(),
                stopPropagation: () => e.stopPropagation(),
                currentTarget: e.currentTarget,
                target: e.target,
              } as unknown as React.FormEvent<HTMLFormElement>;
              void handleSubmit(syntheticEvent);
            }}
                          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {loading ? t('businessProfile.buttons.saving') : isEditMode ? t('businessProfile.buttons.updateProfile') : t('businessProfile.buttons.createProfile')}
                        </Button>
                      </Box>
              </Box>

      {/* Confirmation Dialog for Strategy Replacement */}
      <QuotaConfirmationDialog
        open={quotaDialogOpen}
        onClose={handleQuotaCancel}
        onConfirm={handleQuotaConfirm}
        usage={usage}
        generationType="content-strategy"
      />

      <StrategyConfirmationDialog
        open={strategyConfirmDialogOpen}
        onClose={handleCancelStrategyReplace}
        onConfirm={handleConfirmStrategyReplace}
        currentTargetAudience={formData.target_audience}
        currentKeyMessages={formData.key_messages}
        currentContentGoals={formData.content_goals}
        currentBrandVoice={Array.isArray(formData.brand_voice) ? formData.brand_voice : []}
        newTargetAudience={generatedStrategy?.target_audience}
        newKeyMessages={generatedStrategy?.key_messages}
        newContentGoals={generatedStrategy?.content_goals}
        newBrandVoice={generatedStrategy?.brand_voice}
      />

      {/* Save/Discard Dialog for Navigation Blocking */}
      <SaveDiscardDialog
        open={saveDiscardDialogOpen}
        onClose={handleCancelNavigation}
        onSave={handleSaveAndNavigate}
        onDiscard={handleDiscardAndNavigate}
        isSaving={loading}
      />
</PageSection>
  );
}

export default BusinessProfileForm;

